import { Box, Typography, Button, Divider } from "@mui/material";
import { PrescriptionResponseDtoStatusEnum } from "libs/cellera-sdk";
import React from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { PrescriptionItem } from "./PrescriptionItemsTable";
import { StatusTag } from "./StatusTag";

interface FeesAndTotalSummaryProps {
  medicineItems: PrescriptionItem[];
  formulaTotal: number;
  status: PrescriptionResponseDtoStatusEnum;
  onViewInvoice?: () => void;
}

export const FeesAndTotalSummary: React.FC<FeesAndTotalSummaryProps> = ({
  medicineItems,
  formulaTotal,
  status,
  onViewInvoice,
}) => {
  const { colors } = useCelleraTheme();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const calculateSubtotal = (items: PrescriptionItem[]) => {
    return items.reduce((sum, item) => sum + item.price, 0);
  };

  const medicineSubtotal = calculateSubtotal(medicineItems);
  const subtotal = medicineSubtotal + formulaTotal;
  const taxRate = 0.05;
  const tax = subtotal * taxRate;
  const discount = 1.0;
  const total = subtotal + tax - discount;

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        p: "16px",
        backgroundColor: colors.background.paper,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
          mb: 3,
        }}
      >
        Fees & Total Summary
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 1 }}
        >
          Payment Status:
        </Typography>
        <StatusTag status={status} />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Subtotal (Medicine):
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.primary }}>
            {formatPrice(medicineSubtotal)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Subtotal (Supplement):
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.primary }}>
            {formatPrice(formulaTotal)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Tax / Fee (5%):
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.primary }}>
            {formatPrice(tax)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Discount:
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.error[500], fontWeight: 500 }}
          >
            -{formatPrice(discount)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            Total:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            {formatPrice(total)}
          </Typography>
        </Box>
      </Box>

      {onViewInvoice && (
        <Box>
          <Divider />
          <Button
            className="py-0 px-3 rounded-full"
            sx={{
              mt: 2,
              backgroundColor: colors.background.paper,
              color: colors.text.primary,
              border: `1px solid ${colors.border.main}`,
            }}
            onClick={onViewInvoice}
          >
            View Invoice
          </Button>
        </Box>
      )}
    </Box>
  );
};
